import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, ChevronRight, ChevronLeft } from 'lucide-react';
import AcademyNav from '../../components/academy/AcademyNav';
import ArticleCard from '../../components/academy/ArticleCard';
import CtaBand from '../../components/academy/CtaBand';
import Seo from '../../components/academy/Seo';
import BrandHero from '../../components/BrandHero';
import { getCategories, getArticles } from '../../lib/academyApi';

const AcademyAllArticles = () => {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeCat, setActiveCat] = useState(params.get('category') || '');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ articles: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
    if (params.get('focus') === 'search') {
      setTimeout(() => searchRef.current?.focus(), 300);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => { setPage(1); }, [debounced, activeCat]);

  useEffect(() => {
    setLoading(true);
    const p = { page, limit: 12 };
    if (debounced) p.search = debounced;
    if (activeCat) p.category = activeCat;
    getArticles(p).then(setData).finally(() => setLoading(false));
  }, [debounced, activeCat, page]);

  const selectCat = (slug) => {
    setActiveCat(slug);
    const next = new URLSearchParams(params);
    if (slug) next.set('category', slug); else next.delete('category');
    next.delete('focus');
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0e0e10 0%, #17171a 55%, #0e0e10 100%)' }}>
      <Seo
        title="All Articles & Search"
        description="Search and browse every CarLens Academy article on buying, inspecting and owning used vehicles in Florida."
        path="/academy/articles"
        breadcrumbs={[{ name: 'Academy', path: '/academy' }, { name: 'All Articles', path: '/academy/articles' }]}
      />
      <BrandHero />
      <AcademyNav />

      <div className="max-w-7xl mx-auto px-6 py-12 md:py-16 space-y-10">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            All Articles
          </h1>
          <p className="mt-3 text-lg text-zinc-400">Search the full CarLens Academy library.</p>
        </div>

        {/* Search bar */}
        <div className="relative max-w-2xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles, topics, keywords…"
            className="w-full pl-12 pr-11 py-4 rounded-2xl bg-[#161618] border border-zinc-800 text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500/60 focus:ring-2 focus:ring-orange-500/20 transition"
            data-testid="academy-search-input"
          />
          {query && (
            <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white" data-testid="academy-search-clear">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-2" data-testid="academy-category-filters">
          <button
            onClick={() => selectCat('')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${!activeCat ? 'bg-orange-500 text-white' : 'bg-white/5 text-zinc-400 hover:text-white'}`}
            data-testid="filter-all"
          >
            All
          </button>
          {categories.filter((c) => c.slug !== 'videos').map((c) => (
            <button
              key={c.slug}
              onClick={() => selectCat(c.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCat === c.slug ? 'bg-orange-500 text-white' : 'bg-white/5 text-zinc-400 hover:text-white'}`}
              data-testid={`filter-${c.slug}`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* Results */}
        {loading ? (
          <p className="text-zinc-500 text-center py-20">Loading…</p>
        ) : data.articles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-zinc-300 text-lg">No results found.</p>
            <p className="text-zinc-500 mt-1">Try a different keyword or category.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-zinc-500" data-testid="academy-result-count">{data.total} {data.total === 1 ? 'result' : 'results'}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.articles.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
            {data.pages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-2">
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-zinc-700 text-sm text-zinc-300 disabled:opacity-40 hover:border-zinc-500 transition-colors">
                  <ChevronLeft className="w-4 h-4" /> Prev
                </button>
                <span className="text-sm text-zinc-400 px-2">Page {data.page} of {data.pages}</span>
                <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-zinc-700 text-sm text-zinc-300 disabled:opacity-40 hover:border-zinc-500 transition-colors">
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}

        <CtaBand />
      </div>
    </div>
  );
};

export default AcademyAllArticles;
